import { useAtomValue, useSetAtom } from 'jotai';
import { startCase } from 'lodash';
import { useMatch } from 'react-router-dom';
import { trackObjectChangeEvent, trackObjectCreationEvent } from '@/analytics/snowplow/analytics';
import { genderAtom } from '@/atoms/queries/userConfigs';
import {
  closeAndResetRadarTemplateModalAtom,
  radarTemplateNameAtom,
  radarTemplatePositionGroupAtom,
  unwrappedRadarTemplateDistributionsAtom,
} from '@/atoms/radar';
import { toastDataAtom } from '@/atoms/toast';
import { userConfigActionAtom, userConfigIdAtom } from '@/atoms/userConfigs';
import { UserConfigActions, UserConfigType } from '@/types/userConfigs';
import { useNavigateToRadarTemplateId } from './useNavigateToRadarTemplateId';
import { useUpsertUserConfig } from './useUpsertUserConfig';

export const useUpsertRadarTemplate = () => {
  const upsertUserConfig = useUpsertUserConfig();
  const navigateToRadarTemplateId = useNavigateToRadarTemplateId();
  const setToastData = useSetAtom(toastDataAtom);
  const closeAndResetRadarTemplateModal = useSetAtom(closeAndResetRadarTemplateModalAtom);
  const userConfigIdToAmend = useAtomValue(userConfigIdAtom);
  const userConfigAction = useAtomValue(userConfigActionAtom);
  const radarTemplateName = useAtomValue(radarTemplateNameAtom);
  const isPlayerSection = !!useMatch('/:locale/player/*');
  const radarTemplatePositionGroup = useAtomValue(radarTemplatePositionGroupAtom);
  const radarTemplateDistributions = useAtomValue(unwrappedRadarTemplateDistributionsAtom);
  const entity = isPlayerSection ? 'player' : 'team';
  const gender = useAtomValue(genderAtom);
  const trackingContext = `${startCase(entity)} - Custom Radar`;

  const upsertRadarTemplate = async () => {
    const radarTemplateDefinition = {
      entity,
      gender,
      positionGroup: isPlayerSection ? radarTemplatePositionGroup : null,
      titleSettings: {
        title: radarTemplateName.trim(),
        translate: false,
      },
      metrics: radarTemplateDistributions,
    };

    const { configIds, isSuccess } = await upsertUserConfig({
      configId: userConfigIdToAmend ?? undefined,
      configType: UserConfigType.RADAR_TEMPLATE,
      configDefinition: radarTemplateDefinition,
    });

    setToastData({
      toastName: 'saveRadarTemplate',
      type: isSuccess ? 'success' : 'error',
      entityName: radarTemplateName,
    });

    const configId = configIds?.[0];
    if (isSuccess && configId) {
      if (userConfigIdToAmend && userConfigAction === UserConfigActions.EDIT) {
        trackObjectChangeEvent({ objectType: 'RuleSet', context: trackingContext, changeType: 'Edit' });
      }

      if (!userConfigIdToAmend && userConfigAction === UserConfigActions.DUPLICATE) {
        trackObjectChangeEvent({ objectType: 'RuleSet', context: trackingContext, changeType: 'Duplicate' });
      }

      // we only want to navigate when a new radar template is created
      if (!userConfigIdToAmend) {
        trackObjectCreationEvent({ objectType: 'RuleSet', context: trackingContext, name: radarTemplateName });
        navigateToRadarTemplateId(configId);
      }

      closeAndResetRadarTemplateModal();
    }
  };

  return upsertRadarTemplate;
};
